import { useState, useEffect } from "react";
import type { OptimizeResumeBaseEventDto } from "@reactive-resume/dto";

export const useSubscribeToOptimizerQueue = <
  T extends OptimizeResumeBaseEventDto = OptimizeResumeBaseEventDto,
>(
  resumeId: string,
) => {
  const [events, setEvents] = useState<T[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [status, setStatus] = useState<"idle" | "connecting" | "connected" | "error" | "completed">(
    "idle",
  );

  useEffect(() => {
    if (!resumeId) return;

    setStatus("connecting");
    const eventSource = new EventSource(`/api/resume-optimizer/subscribe/${resumeId}`, {
      withCredentials: true,
    });

    eventSource.onopen = () => {
      setStatus("connected");
    };

    eventSource.onerror = (event) => {
      console.error("EventSource error:", event);
      setStatus("error");
      setError(new Error("Failed to connect to optimization event stream"));
      eventSource.close();
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as T;
        setEvents((prev) => [...prev, data]);
        if (data.type === "completed") {
          eventSource.close();
          setStatus("completed");
        }
      } catch (error) {
        console.error("Error parsing event data:", error);
      }
    };

    return () => {
      eventSource.close();
      setStatus("idle");
    };
  }, [resumeId]);

  const reset = () => {
    setEvents([]);
    setError(null);
    setStatus("idle");
  };

  return { events, error, status, reset };
};
